import { useContext, useMemo, useState } from 'react';

import { CustomModal } from '@/components/Shared/Modal/CustomModal';
import { SapaBankStatus } from '@/components/Shared/constants/banks-statuses';
import { Button, InfoBadge, OfferCard } from '@/components/Shared/ui';
import { useAppSelector } from '@/store';

import { UpdateStatusContext } from '../../../entities';
import { AlternativeItem, OfferDetails } from '../../entities';
import { getOfferBodyProducts } from '../../helpers/offerBodyProduct';
import { OfferStepContext } from '../../offer-step';
import type { TBankOffers } from '../../types/bank-offer.types';
import useFormulateOfferItem from './hooks/useFormulateOfferItem';
import useHeaderActions from './hooks/useHeaderActions';

interface IProps {
  bankOffer: TBankOffers;
  hasOfferedResponse?: boolean;
}

const SapaApprovedItem: React.FC<IProps> = ({
  bankOffer: initialBankOffer,
  hasOfferedResponse
}) => {
  const { banksOffers } = useAppSelector((state) => state.offerStep);
  const {
    userAccesses: {
      global: { isAdmin }
    }
  } = useAppSelector((state) => state.userInfo);

  const { handleCancelResponse } = useContext(OfferStepContext);
  const { onUpdateOfferStatus } = useContext(UpdateStatusContext);

  const [cancelModal, setCancelModal] = useState(false);

  const {
    additionalProducts,
    bankOffer,
    getDescription,
    headerIcon,
    headerTitle,
    isBlueColoredStatus,
    isCurrentResponseOffered,
    isMobile,
    offerDetailsModal,
    setOfferDetailsModal
  } = useFormulateOfferItem({
    bankOffer: initialBankOffer,
    hasOfferedResponse
  });

  const { headerActions, showAlternatives } = useHeaderActions({
    bankOffer,
    setOfferDetailsModal,
    setCancelModal
  });

  const isHasFinancedOffer = useMemo(
    () =>
      banksOffers.some(
        (offer) => offer.status.code === SapaBankStatus.Financed
      ),
    [banksOffers]
  );

  return (
    <>
      <OfferCard
        // Стили для классов bank-item... расположены в widget BankOffers
        className="bank-item"
        status={isBlueColoredStatus ? 'processing' : 'success'}
        extId={bankOffer.extId || ''}
        isSelected={bankOffer.isOffered && !isBlueColoredStatus}
        statusText={bankOffer.status.name}
        onEditStatus={
          !isHasFinancedOffer && isAdmin
            ? () => onUpdateOfferStatus(bankOffer.id)
            : undefined
        }
      >
        <OfferCard.Header
          title={headerTitle}
          icon={headerIcon}
          headerActions={
            !hasOfferedResponse || isCurrentResponseOffered ? headerActions : []
          }
          info={
            hasOfferedResponse && !isCurrentResponseOffered
              ? 'Есть принятое предложение'
              : undefined
          }
          requestBodyInfo={{
            bankName: bankOffer.bank.name,
            responsePrimaryBody: bankOffer.responsePrimaryBody,
            responseSendedBody: bankOffer.responseSendedBody
          }}
        />
        {bankOffer.status.code === SapaBankStatus.AcceptedAlternative && (
          <div className="bank-item__info-badge">
            <InfoBadge title="Данное предложение является альтернативой" />
          </div>
        )}

        <OfferCard.Body
          footerActions={headerActions}
          additionalProducts={additionalProducts}
          products={getOfferBodyProducts({
            monthlyPayment: bankOffer.monthlyPayment,
            rate: bankOffer.rate,
            loanPeriod: bankOffer.loanPeriod,
            loanAmount: bankOffer.loanAmount,
            initialPayment: bankOffer.initialPayment,
            bankProduct: bankOffer.product,
            isMobile
          })}
        />

        {showAlternatives &&
          !isMobile &&
          bankOffer.alternatives?.map((alternativeItem) => (
            <AlternativeItem
              alternativeInfo={alternativeItem}
              bank={bankOffer}
              hasOfferedResponse={!!hasOfferedResponse}
              headerIcon={headerIcon}
              headerTitle={headerTitle}
              isBlueColoredStatus={isBlueColoredStatus}
              isCurrentResponseOffered={!!isCurrentResponseOffered}
              isMobile={isMobile}
              key={alternativeItem.extUuid}
              setOfferDetailsModal={setOfferDetailsModal}
            />
          ))}
      </OfferCard>

      {isMobile &&
        bankOffer.alternatives?.map((alternativeItem) => (
          <AlternativeItem
            alternativeInfo={alternativeItem}
            bank={bankOffer}
            hasOfferedResponse={!!hasOfferedResponse}
            headerIcon={headerIcon}
            headerTitle={headerTitle}
            isBlueColoredStatus={isBlueColoredStatus}
            isCurrentResponseOffered={!!isCurrentResponseOffered}
            isMobile={isMobile}
            key={alternativeItem.extUuid}
            setOfferDetailsModal={setOfferDetailsModal}
          />
        ))}

      <OfferDetails
        bankCode={bankOffer.bank.name}
        bankResponse={{
          allResponse: getDescription()
        }}
        historyData={{
          bank: bankOffer.bank.name.replace(/_/g, ''),
          offerId: bankOffer.id
        }}
        isOpened={!!offerDetailsModal}
        onClose={setOfferDetailsModal}
      />

      <CustomModal
        editClass="cancel-offer-modal"
        isModalOpen={cancelModal}
        handleOk={() => {
          handleCancelResponse(bankOffer);
          setCancelModal(false);
        }}
        handleCancel={() => setCancelModal(false)}
        title="Вы уверены что хотите отклонить предложения данного банка?"
        footer={[
          <Button key="back" onClick={() => setCancelModal(false)}>
            Нет
          </Button>,
          <Button
            key="submit"
            variant="filled-red"
            onClick={() => {
              handleCancelResponse(bankOffer);
              setCancelModal(false);
            }}
          >
            Да, отменить
          </Button>
        ]}
      />
    </>
  );
};

export default SapaApprovedItem;
